import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { Network, Shield, Building2, FileText, HandshakeIcon, ArrowRight, Sparkles, Eye, Zap, Globe, Award, Heart, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(true);
  const ref = useRef(null);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const COPPAMGaranties = () => {
  const [heroRef, heroInView] = useInView();
  const [ecosystemRef, ecosystemInView] = useInView();
  const [servicesRef, servicesInView] = useInView();
  const [partnersRef, partnersInView] = useInView();

  const ecosystem = [
    { icon: HandshakeIcon, title: 'COPPAM', subtitle: 'Partenaire — Garanties solidaires', desc: 'Accueil, pré-diagnostic, accompagnement et médiation. Partenaire distinct d’O’SCOP, avec des responsabilités propres définies dans le cadre de la coopération.', gradient: 'from-green-500 to-emerald-500', features: ['Accueil personnalisé', 'Médiation sociale', 'Montage des dossiers'] },
    { icon: FileText, title: 'F.O.G.E.D.O.M', subtitle: 'Partenaire associatif — Instruction', desc: 'Instruction sociale, territoriale et technique des demandes. Les relations avec la SCIC sont organisées par convention.', gradient: 'from-blue-500 to-cyan-500', features: ['Analyse technique', 'Vérification terrain', 'Rapport d\'instruction'] },
    { icon: Eye, title: 'FOGEDOM-SCIC', subtitle: 'Fonds interne d’O’SCOP', desc: 'Section analytique de la SCIC sans personnalité juridique distincte. Soutien, prévention et ingénierie selon les statuts et le règlement du fonds.', gradient: 'from-purple-500 to-pink-500', features: ['Instruction des demandes', 'Suivi des engagements', 'Comptabilité dédiée'] },
    { icon: Building2, title: 'SCIC O\'SCOP', subtitle: 'Gouvernance', desc: 'Gestion administrative, facturation, encaissement, coordination et supervision du dispositif multi-acteurs.', gradient: 'from-amber-500 to-orange-500', features: ['Administration', 'Coordination', 'Supervision'] },
  ];

  const services = [
    { icon: Shield, title: 'BATICO — RCDS', desc: 'Responsabilité civile et décennale solidaire', gradient: 'from-blue-500 to-cyan-500' },
    { icon: Building2, title: 'NOMINATIVE — RCDS au chantier', desc: 'Accompagnement d’un chantier identifié', gradient: 'from-green-500 to-emerald-500' },
    { icon: Layers, title: 'DOMIUM (DOS)', desc: 'Entraide et prévention après travaux', gradient: 'from-purple-500 to-pink-500' },
    { icon: Award, title: 'CAP BÂTI', desc: 'Accompagnement des constructions de maisons individuelles', gradient: 'from-amber-500 to-orange-500' },
    { icon: Zap, title: 'CAP BÂTI+', desc: 'Accompagnement des difficultés de livraison', gradient: 'from-red-500 to-rose-500' },
    { icon: Heart, title: 'ARCHOS — RCDS', desc: 'Soutien aux professions intellectuelles du bâtiment', gradient: 'from-indigo-500 to-purple-500' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/30 mb-6 px-4 py-2">
              <Network className="w-4 h-4 mr-2" /> Écosystème intégré
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-purple-100 to-white bg-clip-text text-transparent">L'Écosystème</span>
              <br />
              <span className="text-3xl md:text-4xl lg:text-5xl bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">COPPAM / FOGEDOM</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mb-10 leading-relaxed">
              Un écosystème <span className="text-purple-400 font-semibold">unique et intégré</span> de garanties solidaires, 
              basé sur la coopération, le droit civil et l'entraide mutuelle.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-purple-500/25 hover:shadow-purple-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/adhesion'}>
                Rejoindre l'écosystème <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section ref={ecosystemRef} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${ecosystemInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 mb-4 px-4 py-2"><Sparkles className="w-4 h-4 mr-2" /> Des rôles complémentaires</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Une <span className="text-purple-400">gouvernance partagée</span></h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {ecosystem.map((item, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${ecosystemInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="flex items-start gap-6">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}>
                    <item.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-1">{item.title}</h3>
                    <p className="text-sm text-slate-400 mb-3">{item.subtitle}</p>
                    <p className="text-slate-300 mb-4 leading-relaxed">{item.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {item.features.map((feature, i) => (
                        <span key={i} className="text-xs bg-slate-700/50 text-slate-300 px-3 py-1 rounded-full">{feature}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-amber-600/10 to-transparent rounded-full blur-3xl" /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-1000 ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 mb-4 px-4 py-2"><Shield className="w-4 h-4 mr-2" /> Nos garanties</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Des solutions <span className="text-amber-400">adaptées</span></h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">Les conditions d’accès et d’intervention de chaque dispositif sont précisées avant tout engagement.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, index) => (
              <div key={index} className={`group bg-slate-800/50 backdrop-blur-xl rounded-2xl p-5 border border-slate-700/50 hover:border-amber-500/50 transition-all duration-500 hover:-translate-y-2 text-center ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 50}ms` }}>
                <div className={`w-12 h-12 mx-auto rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg`}>
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-sm font-bold text-white mb-1">{service.title}</h3>
                <p className="text-xs text-slate-500">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section ref={partnersRef} className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${partnersInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-green-500/20 text-green-300 border border-green-500/30 mb-4 px-4 py-2"><Globe className="w-4 h-4 mr-2" /> Territoires</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Vos projets en <span className="text-green-400">Outre-mer</span></h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">Contactez-nous pour connaître les prestations et les modalités d’intervention adaptées à votre territoire.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {['Guadeloupe', 'Martinique', 'Guyane', 'La Réunion', 'Mayotte'].map((territory, index) => (
              <div key={index} className={`flex items-center gap-3 bg-slate-800/50 backdrop-blur-xl px-6 py-4 rounded-2xl border border-slate-700/50 hover:border-green-500/50 transition-all ${partnersInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-500" />
                <span className="text-white font-medium">{territory}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Intégrez l'écosystème <span className="text-purple-200">COPPAM</span></h2>
          <p className="text-xl text-white/80 mb-10">Bénéficiez de garanties solidaires adaptées à vos projets.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-purple-600 hover:bg-slate-100 font-bold px-10 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/adhesion'}>
              Devenir membre <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg transition-all duration-300" onClick={() => window.location.href = '/contact'}>
              Nous contacter
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default COPPAMGaranties;
